import { geocodeAddress } from "../utils/geo";
import { database } from "../firebase";
import { ref, get } from "firebase/database";

// Cache pour les suggestions d'adresses
const addressCache = new Map();

// Cache pour les adresses de covoiturage personnalisées
let carpoolLocationsCache = null;
let carpoolLocationsCacheTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// API d'autocomplétion d'adresse (utilisant l'API gratuite de l'Adresse)
const ADDRESS_API_BASE = "https://api-adresse.data.gouv.fr/search/";

/**
 * Récupérer les adresses de covoiturage personnalisées depuis Firebase
 */
async function getCarpoolLocations() {
  const now = Date.now();
  
  // Utiliser le cache si disponible et récent
  if (carpoolLocationsCache && (now - carpoolLocationsCacheTime) < CACHE_DURATION) {
    return carpoolLocationsCache;
  }

  try {
    const locationsRef = ref(database, 'carpoolLocations');
    const snapshot = await get(locationsRef);
    
    if (snapshot.exists()) {
      const locations = [];
      snapshot.forEach((childSnapshot) => {
        locations.push({
          id: `carpool_${childSnapshot.key}`,
          ...childSnapshot.val(),
        });
      });
      carpoolLocationsCache = locations;
      carpoolLocationsCacheTime = now;
      return locations;
    }
    
    carpoolLocationsCache = [];
    carpoolLocationsCacheTime = now;
    return [];
  } catch (error) {
    console.warn("Erreur récupération adresses covoiturage:", error);
    return carpoolLocationsCache || [];
  }
}

/**
 * Normaliser une requête de recherche (enlever accents, normaliser espaces, etc.)
 */
function normalizeQuery(query) {
  return query
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Enlever les accents
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Générer des variantes de recherche pour améliorer les résultats
 */
function generateSearchVariants(query) {
  const variants = [query];
  
  // Ajouter "rue" si pas présent
  if (!query.toLowerCase().includes("rue") && !query.toLowerCase().includes("avenue") && !query.toLowerCase().includes("boulevard") && !query.toLowerCase().includes("chemin") && !query.toLowerCase().includes("place")) {
    variants.push(`rue ${query}`);
  }
  
  // Enlever "rue" si présent pour chercher aussi sans
  const withoutRue = query.replace(/^(rue|avenue|boulevard|chemin|place|impasse|allée)\s+/i, "").trim();
  if (withoutRue !== query && withoutRue.length >= 3) {
    variants.push(withoutRue);
  }
  
  // Ajouter Blois si pas de ville dans la requête
  if (!query.toLowerCase().includes("blois") && !query.toLowerCase().includes("41000")) {
    variants.push(`${query} Blois`);
    variants.push(`${query} 41000`);
  }
  
  return variants;
}

/**
 * Recherche d'adresses avec autocomplétion améliorée
 * Utilise plusieurs stratégies pour trouver toutes les rues
 * Privilégie les adresses du Loir-et-Cher (41)
 */
export async function searchAddresses(query, limit = 10) {
  if (!query || query.length < 3) {
    return [];
  }

  // Vérifier le cache d'abord
  const cacheKey = normalizeQuery(query);
  if (addressCache.has(cacheKey)) {
    return addressCache.get(cacheKey);
  }

  try {
    let allAddresses = [];
    const seenIds = new Set();
    const normalizedQuery = normalizeQuery(query);

    // Recherche 0: Adresses de covoiturage personnalisées (priorité maximale)
    try {
      const carpoolLocations = await getCarpoolLocations();
      carpoolLocations.forEach((location) => {
        const locationName = normalizeQuery(location.name);
        const locationAddress = normalizeQuery(location.address || "");
        
        // Vérifier si la requête correspond au nom ou à l'adresse
        const nameMatch = locationName.includes(normalizedQuery) || normalizedQuery.includes(locationName);
        const addressMatch = locationAddress && (locationAddress.includes(normalizedQuery) || normalizedQuery.includes(locationAddress));
        
        if (nameMatch || addressMatch) {
          const id = location.id;
          if (!seenIds.has(id)) {
            seenIds.add(id);
            
            // Construire l'adresse complète
            const fullAddress = location.address 
              ? `${location.address}, ${location.postalCode} ${location.city}`
              : `${location.name}, ${location.postalCode} ${location.city}`;
            
            // Calculer un score de priorité basé sur la correspondance
            // Les parkings de covoiturage ont TOUJOURS la priorité maximale
            // Correspondance exacte du nom = priorité maximale (5000)
            // Correspondance au début du nom = priorité très élevée (4500)
            // Correspondance partielle du nom = priorité élevée (4000)
            // Correspondance de l'adresse = priorité élevée (3500)
            let priorityScore = 3500;
            if (nameMatch) {
              if (locationName === normalizedQuery) {
                priorityScore = 5000; // Correspondance exacte
              } else if (locationName.startsWith(normalizedQuery)) {
                priorityScore = 4800; // Le nom commence par la requête (ex: "Parking Mon" → "Parking Monsabré")
              } else if (normalizedQuery.startsWith(locationName)) {
                priorityScore = 4700; // La requête commence par le nom
              } else {
                priorityScore = 4000; // Correspondance partielle
              }
            }
            
            allAddresses.push({
              id,
              fullAddress,
              street: location.address || location.name, // Adresse réelle pour le géocodage
              carpoolLocationName: location.name, // Nom à afficher dans le champ adresse
              realAddress: location.address || "", // Adresse réelle stockée en arrière-plan
              housenumber: "",
              postcode: location.postalCode,
              city: location.city,
              department: "41",
              isLoirEtCher: true,
              isCarpoolLocation: true, // Marqueur pour les adresses personnalisées
              context: `Parking de covoiturage`,
              score: 1.0,
              priorityScore, // Priorité dynamique selon la correspondance
              latitude: location.latitude,
              longitude: location.longitude,
            });
          }
        }
      });
    } catch (carpoolError) {
      console.warn("Erreur recherche adresses covoiturage (non bloquant):", carpoolError);
    }

    // Générer des variantes de recherche
    const variants = generateSearchVariants(query);
    
    // Rechercher avec chaque variante
    for (const variant of variants) {
      try {
        // Recherche 1: API Adresse Data Gouv avec 41
        const query41 = `${variant} 41`;
        const response41 = await fetch(
          `${ADDRESS_API_BASE}?q=${encodeURIComponent(query41)}&limit=${limit * 2}`,
        );
        const data41 = await response41.json();

        if (data41.features) {
          data41.features.forEach((feature) => {
            const properties = feature.properties;
            const postcode = properties.postcode || "";
            
            // Seulement le 41
            if (postcode.startsWith("41")) {
              const id = feature.properties.id;
              if (!seenIds.has(id)) {
                seenIds.add(id);
                allAddresses.push({
                  id,
                  fullAddress: feature.properties.label,
                  street: properties.street || "",
                  housenumber: properties.housenumber || "",
                  postcode,
                  city: properties.city || "",
                  department: "41",
                  isLoirEtCher: true,
                  context: properties.context || "",
                  score: properties.score || 0,
                  priorityScore: (properties.score || 0) + 1000,
                });
              }
            }
          });
        }

        // Recherche 2: API Adresse Data Gouv sans restriction département
        const responseAll = await fetch(
          `${ADDRESS_API_BASE}?q=${encodeURIComponent(variant)}&limit=${limit * 2}`,
        );
        const dataAll = await responseAll.json();

        if (dataAll.features) {
          dataAll.features.forEach((feature) => {
            const properties = feature.properties;
            const postcode = properties.postcode || "";
            
            // Seulement le 41
            if (postcode.startsWith("41")) {
              const id = feature.properties.id;
              if (!seenIds.has(id)) {
                seenIds.add(id);
                allAddresses.push({
                  id,
                  fullAddress: feature.properties.label,
                  street: properties.street || "",
                  housenumber: properties.housenumber || "",
                  postcode,
                  city: properties.city || "",
                  department: "41",
                  isLoirEtCher: true,
                  context: properties.context || "",
                  score: properties.score || 0,
                  priorityScore: (properties.score || 0) + 500,
                });
              }
            }
          });
        }

        // Recherche 3: Nominatim (OpenStreetMap) en complément pour les rues manquantes
        try {
          const nominatimQuery = `${variant}, Blois, France`;
          const nominatimResponse = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(nominatimQuery)}&limit=5&addressdetails=1&countrycodes=fr`,
            {
              headers: {
                'User-Agent': 'CovoiturageApp/1.0'
              }
            }
          );
          const nominatimData = await nominatimResponse.json();

          if (Array.isArray(nominatimData)) {
            nominatimData.forEach((item) => {
              const postcode = item.address?.postcode || "";
              if (postcode.startsWith("41")) {
                const street = item.address?.road || item.address?.pedestrian || "";
                const city = item.address?.city || item.address?.town || "Blois";
                const housenumber = item.address?.house_number || "";
                
                if (street) {
                  const fullAddress = housenumber 
                    ? `${housenumber} ${street}, ${postcode} ${city}`
                    : `${street}, ${postcode} ${city}`;
                  
                  const id = `nominatim_${item.place_id}`;
                  if (!seenIds.has(id)) {
                    seenIds.add(id);
                    allAddresses.push({
                      id,
                      fullAddress,
                      street,
                      housenumber,
                      postcode,
                      city,
                      department: "41",
                      isLoirEtCher: true,
                      context: "",
                      score: parseFloat(item.importance || 0.5),
                      priorityScore: parseFloat(item.importance || 0.5) * 100 + 200, // Priorité moyenne pour Nominatim
                    });
                  }
                }
              }
            });
          }
        } catch (nominatimError) {
          console.warn("Erreur Nominatim (non bloquant):", nominatimError);
        }

      } catch (variantError) {
        console.warn(`Erreur recherche variante "${variant}":`, variantError);
      }
    }

    // Trier par priorité et score
    allAddresses.sort((a, b) => b.priorityScore - a.priorityScore);

    // Limiter aux meilleurs résultats
    const topResults = allAddresses.slice(0, limit);

    // Mettre en cache
    addressCache.set(cacheKey, topResults);

    return topResults;
  } catch (error) {
    console.error("Erreur lors de la recherche d'adresses:", error);
    return [];
  }
}

/**
 * Validation d'une adresse complète
 * Vérifie la cohérence entre adresse, code postal et ville
 */
export async function validateAddress(address, postcode, city) {
  const errors = [];
  const warnings = [];

  // Vérifications de base
  if (!address || address.trim().length < 5) {
    errors.push("L'adresse doit contenir au moins 5 caractères");
  }

  if (!postcode || !/^\d{5}$/.test(postcode)) {
    errors.push("Le code postal doit contenir exactement 5 chiffres");
  }

  if (!city || city.trim().length < 2) {
    errors.push("La ville doit contenir au moins 2 caractères");
  }

  // Vérification du département
  if (postcode && postcode.length === 5) {
    const department = postcode.substring(0, 2);
    if (department !== "41") {
      warnings.push(
        `Attention : Cette adresse n'est pas dans le Loir-et-Cher (41). Département détecté : ${department}`,
      );
    }
  }

  // Vérification de cohérence via géocodage
  try {
    const fullAddress = `${address}, ${postcode} ${city}`;
    const geo = await geocodeAddress(fullAddress);

    if (geo) {
      // Vérifier que le code postal et la ville correspondent
      const geocodedPostcode = geo.postcode;
      const geocodedCity = geo.city;

      if (geocodedPostcode && geocodedPostcode !== postcode) {
        warnings.push(
          `Code postal suggéré : ${geocodedPostcode} (saisi : ${postcode})`,
        );
      }

      if (geocodedCity && geocodedCity.toLowerCase() !== city.toLowerCase()) {
        warnings.push(`Ville suggérée : ${geocodedCity} (saisie : ${city})`);
      }
    }
  } catch (error) {
    console.warn("Impossible de valider l'adresse via géocodage:", error);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Suggestions de villes pour un code postal
 */
export async function getCitiesForPostcode(postcode) {
  if (!postcode || postcode.length !== 5) {
    return [];
  }

  try {
    const response = await fetch(
      `${ADDRESS_API_BASE}?postcode=${postcode}&limit=20`,
    );
    const data = await response.json();

    if (!data.features) {
      return [];
    }

    // Extraire les villes uniques
    const cities = [
      ...new Set(data.features.map((f) => f.properties.city)),
    ].filter(Boolean);
    return cities.sort();
  } catch (error) {
    console.error("Erreur lors de la recherche de villes:", error);
    return [];
  }
}

/**
 * Suggestions de codes postaux pour une ville
 */
export async function getPostcodesForCity(city) {
  if (!city || city.length < 2) {
    return [];
  }

  try {
    const response = await fetch(
      `${ADDRESS_API_BASE}?city=${encodeURIComponent(city)}&limit=20`,
    );
    const data = await response.json();

    if (!data.features) {
      return [];
    }

    // Extraire les codes postaux uniques
    const postcodes = [
      ...new Set(data.features.map((f) => f.properties.postcode)),
    ].filter(Boolean);

    // Privilégier le Loir-et-Cher (41)
    const sortedPostcodes = postcodes.sort((a, b) => {
      const aIs41 = a.startsWith("41");
      const bIs41 = b.startsWith("41");
      if (aIs41 && !bIs41) return -1;
      if (!aIs41 && bIs41) return 1;
      return a.localeCompare(b);
    });

    return sortedPostcodes;
  } catch (error) {
    console.error("Erreur lors de la recherche de codes postaux:", error);
    return [];
  }
}

/**
 * Nettoyer le cache des adresses
 */
export function clearAddressCache() {
  addressCache.clear();
}
